/**
 * SocialCapital branded top navigation.
 *
 * Used across all /sc/* pages. Sticky, white background, brand mark on
 * the left (green+blue silhouette powered by Social Pulse), full nav
 * on the right. Falls back to a compact hamburger below md.
 */
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, User } from "lucide-react";

const NAV = [
  { label: "Home",                    to: "/sc",                exact: true },
  { label: "What Is SocialCapital?",  to: "/sc/what-is" },
  { label: "How It Works",            to: "/sc/how-it-works" },
  { label: "Earn",                    to: "/sc/earn" },
  { label: "Passport",                to: "/sc/passport" },
  { label: "Students",                to: "/sc/students" },
  { label: "Careers",                 to: "/sc/careers" },
  { label: "Organizations",           to: "/sc/organizations" },
  { label: "Challenges",              to: "/sc/challenges" },
  { label: "Impact",                  to: "/sc/impact" },
  { label: "Stories",                 to: "/sc/stories" },
  { label: "Legacy",                  to: "/sc/legacy" },
  { label: "Research",                to: "/sc/research" },
  { label: "About",                   to: "/sc/about" },
  { label: "API / Partners",          to: "/sc/api" },
  { label: "My Social Capital",       to: "/sc/me",             highlight: true },
];

const BRAND_GREEN = "#10B981";
const BRAND_BLUE  = "#3B82F6";
const BRAND_PURPLE = "#7C3AED";

function BrandMark({ size = 48 }) {
  // Green leaf + blue figure silhouette, custom SVG so we don't need an asset.
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <path d="M8 44 C 6 26 22 8 42 12 C 40 30 30 42 8 44 Z" fill={BRAND_GREEN} opacity="0.85" />
      <circle cx="36" cy="22" r="7" fill={BRAND_BLUE} />
      <path
        d="M22 54 C 22 42 30 34 36 34 C 42 34 50 42 50 54 Z"
        fill={BRAND_BLUE}
      />
    </svg>
  );
}


export default function SocialCapitalHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="sc-header"
      className="sticky top-0 z-40 border-b bg-white/95 backdrop-blur"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-3 flex items-center gap-4">
        {/* Brand */}
        <Link
          to="/sc"
          data-testid="sc-brand"
          className="flex items-center gap-2.5 shrink-0"
        >
          <BrandMark size={44} />
          <div className="leading-tight">
            <div
              className="font-display text-[22px] tracking-tight flex items-baseline gap-1"
              style={{ color: "#0F172A" }}
            >
              SocialCapital
              <sup className="text-[10px] font-normal opacity-70">TM</sup>
            </div>
            <div className="text-[11px]" style={{ color: BRAND_BLUE }}>
              SocialCapital.cc
            </div>
            <div className="text-[10px] opacity-60">
              Powered by{" "}
              <span style={{ color: BRAND_GREEN, fontWeight: 600 }}>
                Social Pulse ®
              </span>
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav
          data-testid="sc-nav"
          className="ml-auto hidden xl:flex items-center gap-0.5 flex-wrap justify-end"
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={`sc-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `px-2.5 py-2 rounded-md text-[12px] transition ${
                  isActive ? "font-semibold" : "hover:bg-slate-50"
                } ${n.highlight ? "font-semibold" : ""}`
              }
              style={({ isActive }) => (
                n.highlight
                  ? {
                      background: isActive ? BRAND_BLUE : "rgba(59,130,246,0.10)",
                      color: isActive ? "white" : BRAND_BLUE,
                      borderRadius: "9999px",
                      padding: "6px 12px",
                    }
                  : {
                      color: isActive ? BRAND_BLUE : "#334155",
                      borderBottom: isActive ? `2px solid ${BRAND_BLUE}` : "2px solid transparent",
                    }
              )}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Account chip */}
        <Link
          to="/sc/me"
          data-testid="sc-account-chip"
          className="ml-auto xl:ml-2 flex items-center justify-center w-10 h-10 rounded-full border transition hover:shadow-sm shrink-0"
          style={{ borderColor: BRAND_GREEN, background: "white" }}
        >
          <User className="w-4 h-4" style={{ color: BRAND_GREEN }} />
        </Link>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="xl:hidden ml-1 p-2 rounded-md border"
          style={{ borderColor: "#E5E7EB" }}
          data-testid="sc-nav-toggle"
        >
          {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="sc-mobile-nav"
          className="xl:hidden border-t"
          style={{ borderColor: "#E5E7EB" }}
        >
          <div className="max-w-[1400px] mx-auto px-5 py-2 grid grid-cols-2 gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-[13px] ${
                    isActive ? "font-semibold" : ""
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? BRAND_BLUE : "#334155",
                  background: isActive ? "rgba(59,130,246,0.08)" : "transparent",
                })}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export { BRAND_GREEN, BRAND_BLUE, BRAND_PURPLE };
